import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Link variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
const underlineHoverDecoration =
  'underline-offset-4 hover:underline hover:decoration-link-underline-hover';

export const linkVariants = cva(
  [
    'inline items-center gap-2 font-body',
    'transition-link duration-link ease-link',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-focus',
  ],
  {
    variants: {
      weight: {
        regular: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
      variant: {
        primary: [
          'text-primary-700',
          underlineHoverDecoration,
          'hover:text-primary-500',
          'active:text-primary-900',
        ],
        secondary: [
          'text-secondary-700',
          underlineHoverDecoration,
          'hover:text-secondary-500',
          'active:text-secondary-900',
        ],
        tertiary: [
          'text-tertiary-700',
          underlineHoverDecoration,
          'hover:text-tertiary-500',
          'active:text-tertiary-900',
        ],
        default: [
          'text-link-base',
          underlineHoverDecoration,
          'hover:text-link-hover',
        ],
        body: ['text-body', underlineHoverDecoration, 'hover:text-body'],
        black: [
          'text-black',
          underlineHoverDecoration,
          'hover:text-neutral-700',
          'active:text-neutral-900',
        ],
        white: [
          'text-white',
          underlineHoverDecoration,
          'hover:text-neutral-200',
          'active:text-neutral-100',
        ],
        onDark: [
          'text-neutral-100',
          underlineHoverDecoration,
          'hover:text-neutral-100',
          'active:text-neutral-100',
        ],
        subtext: [
          'text-subtext',
          underlineHoverDecoration,
          'hover:text-neutral-700',
          'active:text-neutral-900',
        ],
      },
      size: {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
      },
      underline: {
        always: 'underline',
        hover: 'no-underline hover:underline',
        none: 'no-underline hover:no-underline',
      },
      underlineStyle: {
        thin: '',
        thick: [
          'decoration-link-thickness',
          'hover:decoration-link-thickness-hover active:decoration-link-thickness-hover',
          'active:decoration-link-underline-active',
        ],
      },
    },
    defaultVariants: {
      weight: 'regular',
      variant: 'default',
      size: 'md',
      underline: 'hover',
      underlineStyle: 'thick',
    },
  },
);

export type LinkVariantProps = VariantProps<typeof linkVariants>;
